import { SearchResponse, SearchResponseGroupBy } from "../../model/search"
import {
  ExplorerFilterResponse,
  GroupBySearchTransformedResponse,
  UploadStatus,
} from "../../model/search"

import { api } from "./api"

import { getRange } from "../../util/dateRangeUtils"

const API_URL = import.meta.env.VITE_API_URL

const removeTrailingS = (input: string) => {
  if (input === "html") return "text"
  if (!["videos", "images"].includes(input) || !input) return ""
  return input.endsWith("s") ? input.slice(0, -1) : input
}

export const searchApi = api.injectEndpoints({
  endpoints: (build) => ({
    getDefaultSearch: build.query<
      SearchResponse,
      { page_size: number; status: UploadStatus; page_num?: number }
    >({
      query: (params) => ({
        url: `${API_URL}/query_search/creative`,
        method: "POST",
        body: {
          query: {
            bool: {
              must: [
                {
                  match: {
                    "file_data.upload_status": params.status,
                  },
                },
                {
                  match: {
                    "file_data.file_status": "active",
                  },
                },
              ],
              ...(params.status === "success" && {
                must_not: {
                  exists: {
                    field: "tag_data",
                  },
                },
              }),
            },
          },
          search_from: params.page_num ?? 0,
          size: params.page_size,
          sort: [
            {
              created_at: {
                order: "desc",
              },
            },
          ],
        },
      }),
      providesTags: (result, _, arg) => [{ type: "Tags", id: arg.status }],
    }),
    getExplorerSearch: build.query<
      SearchResponse,
      {
        page_size: number
        status: UploadStatus
        skip: number
        file_type: "videos" | "images" | "all" | "html"
        tagData: ExplorerFilterResponse
        range: string
        sort: Record<string, { order: "asc" | "desc" }>
        aiTag: string
        assetId: string
        assetCategories: string[]
      }
    >({
      query: (params) => ({
        url: `${API_URL}/query_search/creative`,
        method: "POST",
        body: {
          query: {
            bool: {
              must: params.assetId
                ? [
                    {
                      match: {
                        creative_id: params.assetId,
                      },
                    },
                  ]
                : [
                    ...params.tagData,
                    !!params.aiTag.length && {
                      match: {
                        ai_labels: {
                          query: params.aiTag,
                          fuzziness: "AUTO",
                        },
                      },
                    },
                    params.assetCategories.length > 0 && {
                      bool: {
                        should: params.assetCategories.map((category) => ({
                          match: {
                            [`tag_data.${category}`]: true,
                          },
                        })),
                        minimum_should_match: 1,
                      },
                    },
                    {
                      match: {
                        "file_data.upload_status": params.status,
                      },
                    },
                    {
                      match: {
                        "file_data.file_status": "active",
                      },
                    },
                    {
                      exists: {
                        field: "tag_data",
                      },
                    },
                    {
                      range: {
                        created_at: getRange(params.range),
                      },
                    },
                    ...[
                      {
                        wildcard: {
                          "file_data.file_type.raw": {
                            value: `${removeTrailingS(params.file_type)}*`,
                          },
                        },
                      },
                    ],
                  ].filter(Boolean),
            },
          },
          search_from: params.skip ?? 0,
          size: params.page_size,
          sort: [
            { ...params.sort },
            {
              created_at: {
                order: "desc",
              },
            },
          ],
        },
      }),
      providesTags: (result, _, arg) => [{ type: "Tags", id: arg.status }],
    }),
    getGroupBySearch: build.query<
      GroupBySearchTransformedResponse,
      {
        page_size: number
        status: UploadStatus
        // page_num?: number
        tagData: ExplorerFilterResponse
        fieldName: string
        file_type: "videos" | "images" | "all" | "html"
        sort: Record<string, "asc" | "desc">
        aiTag: string
        assetCategories: string[]
      }
    >({
      query: (params) => ({
        url: `${API_URL}/agg_search/creative`,
        method: "POST",
        body: {
          query: {
            bool: {
              must: [
                ...params.tagData,
                !!params.aiTag.length && {
                  match: {
                    ai_labels: {
                      query: params.aiTag,
                      fuzziness: "AUTO",
                    },
                  },
                },
                params.assetCategories.length > 0 && {
                  bool: {
                    should: params.assetCategories.map((category) => ({
                      match: {
                        [`tag_data.${category}`]: true,
                      },
                    })),
                    minimum_should_match: 1,
                  },
                },
                {
                  match: {
                    "file_data.upload_status": params.status,
                  },
                },
                {
                  match: {
                    "file_data.file_status": "active",
                  },
                },
                {
                  exists: {
                    field: "tag_data",
                  },
                },
                ...[
                  {
                    wildcard: {
                      "file_data.file_type.raw": {
                        value: `${removeTrailingS(params.file_type)}*`,
                      },
                    },
                  },
                ],
              ].filter(Boolean),
            },
          },
          after_key: {
            next_page: "",
          },
          aggs: {
            group_by_field: `${params.fieldName}.raw`,
            size: params.page_size,
            // search_from: params.page_num,
            sort: [
              { ...params.sort },
              {
                created_at: "desc",
              },
            ],
          },
        },
      }),
      transformResponse: (response: SearchResponseGroupBy) => {
        return {
          data: response.data.map((D) => ({
            groupName: D.key,
            creativeData: D.hits.map((hit) => hit._source),
          })),
          meta: response.meta,
        }
      },
      providesTags: (result, _, arg) => [{ type: "Tags", id: arg.status }],
    }),
    getParentCount: build.query<
      { count: number },
      { tagName: string; type: string; categoryId: string }
    >({
      query: (params) => ({
        url: `${API_URL}/creative/get_count`,
        method: "POST",
        body: {
          query: {
            bool: {
              must: [
                {
                  terms: {
                    "tag_data.Game.raw": [`${params.tagName}`],
                  },
                },
                {
                  terms: {
                    "tag_data.Parent or Child.raw": ["Parent"],
                  },
                },
                {
                  match: {
                    [`tag_data.${params.categoryId}`]: true,
                  },
                },
                {
                  match: {
                    "file_data.upload_status": "success",
                  },
                },
                {
                  match: {
                    "file_data.file_status": "active",
                  },
                },
                {
                  exists: {
                    field: "tag_data",
                  },
                },
                {
                  range: {
                    created_at: getRange("all"),
                  },
                },
                {
                  wildcard: {
                    "file_data.file_type.raw": {
                      value: `${params.type}*`,
                    },
                  },
                },
              ],
            },
          },
          size: 0,
        },
      }),
    }),
  }),
})

export const {
  useGetDefaultSearchQuery,
  useGetExplorerSearchQuery,
  useGetGroupBySearchQuery,
  useGetParentCountQuery,
  useLazyGetParentCountQuery,
} = searchApi

export const {
  endpoints: {
    getDefaultSearch,
    getExplorerSearch,
    getGroupBySearch,
    getParentCount,
  },
} = searchApi
